/**
 * @file 登录用户状态 Store
 * @description 持久化登录 token，并按需缓存当前登录组员资料。
 */
import { defineStore } from 'pinia'
import { ref, computed, type ComputedRef } from 'vue'
import { getMemberController } from '@/api/generated/member-controller/member-controller'
import { decode } from 'js-base64'
import {
  getToken,
  setToken as setTokenUtil,
  removeToken as removeTokenUtil
} from '@/stores/modules/token'

import type { Member } from '@/api/generated/models'

interface DecodedToken {
  /** 登录用户 ID */
  id: number
}

/**
 * 登录用户 Store。
 * @returns token、当前组员缓存和登录态操作方法。
 */
export const loginUserStore = defineStore('tdm-loginUserStore', () => {
  /** 当前登录 token */
  const token = ref(getToken())

  /**
   * 写入登录 token。
   * @param newToken 新 token。
   * @param rememberMe 是否同步写入 Cookie。
   */
  const setToken = (newToken: string, rememberMe = false): void => {
    token.value = newToken
    setTokenUtil(newToken, rememberMe)
  }

  // 清空 token 时同步清空用户信息。
  const removeToken = (): void => {
    member.value = {}
    token.value = ''
    removeTokenUtil()
  }

  /** 当前登录组员资料 */
  const member = ref<Member>({})

  /** 上次获取当前组员资料的时间戳（毫秒） */
  const lastFetched = ref(0)

  /** 当前组员资料刷新 Promise 锁，用于防止重复请求 */
  let fetchingPromise: Promise<ComputedRef<Member>> | null = null

  /** 当前组员资料缓存有效期，1 分钟 */
  const CACHE_TTL = 1 * 60 * 1000

  /**
   * 检查缓存是否需要刷新
   * @param {boolean} refresh 强制刷新
   * @returns {boolean} 是否需要刷新
   */
  const isMemberNeedRefresh = (refresh = false): boolean => {
    const now = Date.now()
    if (
      member.value &&
      Object.keys(member.value).length > 0 &&
      now - lastFetched.value < CACHE_TTL &&
      !refresh
    ) {
      //  缓存未过期返回true
      return true
    }
    // 过期返回false
    return false
  }

  /**
   *  返回Member的计算属性（只读），同时检查缓存是否过期
   * @param {*} refresh 是否强制刷新
   * @returns {ComputedRef<Member>} member
   */
  const getMemberRef = (refresh = false): ComputedRef<Member> => {
    // 1. 未登录情况下直接返回当前的只读 member (此时 member.value 为 {})
    if (!token.value) {
      return computed(() => member.value)
    }
    // 2. 如果缓存存在且未过期
    if (isMemberNeedRefresh(refresh)) {
      return computed(() => member.value)
    }
    // 异步刷新数据
    void refreshMemberRef()
    return computed(() => member.value)
  }

  /**
   * 刷新一次数据后返回最新数据
   * @returns {Promise<ComputedRef<Member>>} 一个Promise，解析值为只读的 member computed ref。
   */
  const refreshMemberRef = async (): Promise<ComputedRef<Member>> => {
    // 如果一个请求正在进行中，则返回该请求的Promise，让后续调用者等待同一个结果
    if (fetchingPromise) {
      return fetchingPromise
    }

    // 发起新请求，并将Promise存起来
    fetchingPromise = (async () => {
      try {
        const [, encodedPayload] = token.value.split('.')
        const decodedToken: DecodedToken = JSON.parse(decode(encodedPayload))
        const res = await getMemberController().getMemberById(decodedToken.id)
        // 请求获取数据
        member.value = res.data.data as Member
        // 移除敏感字段
        if (member.value) {
          delete (member.value as { password?: string }).password
        }
        // 更新缓存时间
        lastFetched.value = Date.now()
        return computed(() => member.value)
      } finally {
        // 请求结束后，清空Promise锁，以便下次可以发起新请求
        fetchingPromise = null
      }
    })()
    return fetchingPromise
  }

  /**
   * 同步读取当前缓存的登录组员资料。
   * @returns 当前组员资料。
   */
  const getMember = (): Member => {
    return getMemberRef().value
  }

  return {
    token,
    setToken,
    removeToken,
    getMemberRef,
    getMember,
    refreshMemberRef
  }
})

export function isLogin(): boolean {
  if (loginUserStore().token) {
    return true
  }
  return false
}
